import styles from 'css/src/components/pricing/comparison-table.module.scss';

import React from 'react';
import classNames from 'classnames';

import type { Plan, PlanFeatures } from 'js/src/client';
import { BillingCycleEnum } from 'js/src/client';
import Table, { TableBorder } from 'js/src/components/common/ui/Table';
import PricingFeatureTooltip from 'js/src/components/pricing/PricingFeatureTooltip';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import UpgradeButton from 'js/src/components/pricing/UpgradeButton';
import { pluralize } from 'js/src/libs/custom-utils';

const COLUMN_CLASSES: Record<number, string> = {
    0: styles['column-0'],
    1: styles['column-1'],
    2: styles['column-2'],
    3: styles['column-3'],
    4: styles['column-4'],
};

const isFreePlan = (plan: Plan) => plan.price === 0 || plan.name === '';

const isBusinessPlan = (planName: string) =>
    planName === 'Business_v3_Annual' ||
    planName === 'Business_v3' ||
    planName === 'Business_v3_Annual_Promotion' ||
    planName === 'Business_Post_Appsumo_Annual';

const getPlanDisplayName = (plan: Plan) => {
    return plan.display_name;
};

const generateFeatureCell = (title: string, tooltip?: string, icon?: string, color?: string) => {
    if (tooltip && icon) {
        return (
            <td>
                <PricingFeatureTooltip
                    title={title}
                    description={tooltip}
                    icon={icon}
                    color={color}
                    text={title}
                />
            </td>
        );
    }
    return <td>{title}</td>;
};

type Props = {
    plans: Plan[];
    switchToPlan: (plan: Plan) => void;
    handlePlanSwitchError: (error_info: PlanSwitchErrorInfo) => void;
    isAnnualPromo?: boolean;
};

const ComparisonTable = React.memo(function ComparisonTable({
    plans,
    switchToPlan,
    handlePlanSwitchError,
    isAnnualPromo,
}: Props) {
    const generateHeaderRowData = React.useCallback(
        (header: React.ReactNode) => {
            const nodes = [
                <td key="header">{header}</td>,
                ...plans.map((plan) => <td key={plan.name} />),
            ];
            return <tr className="fw-semibold">{nodes}</tr>;
        },
        [plans],
    );

    const generateCustomRowData = React.useCallback(
        (plansParam: Plan[], func: (plan: Plan) => React.ReactNode) => {
            return plansParam.map((plan, index) => {
                let val: React.ReactNode = func(plan);
                if (val === true) {
                    val = (
                        <i
                            translate="no"
                            className={classNames(
                                styles['color-inherit'],
                                'material-symbols-outlined icon-l5 icon-l5--xlarge',
                            )}
                        >
                            check
                        </i>
                    );
                } else if (val === false || val === 0) {
                    val = <span className="text-muted">&#8212;</span>;
                }
                return (
                    <td
                        key={plan.name}
                        className={classNames(COLUMN_CLASSES[index], 'text-center')}
                    >
                        {val}
                    </td>
                );
            });
        },
        [],
    );

    const generateSimpleRowData = React.useCallback(
        (plansParam: Plan[], key: keyof PlanFeatures) => {
            return generateCustomRowData(plansParam, (plan: Plan) => plan.features[key]);
        },
        [generateCustomRowData],
    );

    if (plans === undefined) {
        return null;
    }

    // The "billed yearly" line only appears on annual paid plans. When it is
    // present, free/enterprise columns get an invisible spacer so their CTAs
    // line up with the paid ones; in the monthly view nobody has the line, so
    // the spacers would push those buttons out of alignment instead.
    const showsBilledYearly =
        isAnnualPromo ||
        plans.some((plan) => !plan.is_enterprise && plan.billing_cycle === BillingCycleEnum.ANNUAL);

    return (
        <Table className={styles['comparison-table']} border={TableBorder.None} responsive={true}>
            <thead>
                <tr>
                    <th />
                    {plans.map((plan, index) => {
                        const isFree = isFreePlan(plan);
                        let price_value: string;
                        let price_unit: string | null;
                        if (plan.is_enterprise) {
                            price_value = "Let's talk!";
                            price_unit = null;
                        } else if (isFree) {
                            price_value = '$0';
                            price_unit = '/forever';
                        } else {
                            const price = plan.price;
                            let displayPrice = `${price}`;
                            // For the annual promo page, we display the price per month even though
                            // the plans are annual
                            if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                                const monthlyPrice = price / 12.0;
                                displayPrice =
                                    monthlyPrice % 1 !== 0
                                        ? monthlyPrice.toFixed(2)
                                        : `${monthlyPrice}`;
                            }
                            price_value = `$${displayPrice}`;
                            price_unit = '/mo';
                        }
                        return (
                            <th
                                key={plan.name}
                                className={classNames(
                                    COLUMN_CLASSES[index],
                                    'text-center pt-l5-24 pb-l5-24',
                                )}
                            >
                                <div
                                    className={classNames(
                                        styles['color-inherit'],
                                        'font-l5 font-l5--h5 fw-semibold',
                                    )}
                                >
                                    {getPlanDisplayName(plan)}
                                </div>
                                <div className="font-l5 font-l5--regular">
                                    <span className="fw-bold">{price_value}</span>
                                    {price_unit && (
                                        <span className={styles['price-unit']}>{price_unit}</span>
                                    )}
                                    {(isAnnualPromo ||
                                        plan.billing_cycle === BillingCycleEnum.ANNUAL) &&
                                        !plan.is_enterprise &&
                                        !isFree && (
                                            <>
                                                <br />
                                                <span
                                                    className={classNames(
                                                        'font-l5 font-l5--subtext',
                                                        styles['billed-yearly'],
                                                    )}
                                                >
                                                    billed yearly
                                                </span>
                                            </>
                                        )}
                                    {showsBilledYearly && (plan.is_enterprise || isFree) && (
                                        <>
                                            <br />
                                            <span className="font-l5 font-l5--subtext invisible">
                                                &nbsp;
                                            </span>
                                        </>
                                    )}
                                </div>
                                <div className="mt-l5-16">
                                    <UpgradeButton
                                        key={plan.name}
                                        plan={plan}
                                        switchToPlan={switchToPlan}
                                        handlePlanSwitchError={handlePlanSwitchError}
                                        className="btn btn-extra-small"
                                    />
                                </div>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {generateHeaderRowData('Video creation')}
                <tr>
                    {generateFeatureCell(
                        'Video limits',
                        'Create and publish as many videos as you need, with no restrictions.',
                        'all_inclusive',
                        '#02D3DA',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        const features = plan.features;
                        const numVideosPerMonth = features.num_videos_per_month;
                        if (!numVideosPerMonth) {
                            return 'Unlimited';
                        }
                        if (isAnnualPromo) {
                            return `${numVideosPerMonth * 12}/yr`;
                        }
                        return `${numVideosPerMonth}/mo`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Resolution',
                        'Higher resolution means sharper, more professional-looking videos on any screen.',
                        'hd',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.features.render_1080p) {
                            return '1080p';
                        } else if (plan.features.render_720p) {
                            return '720p';
                        } else {
                            return '480p';
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Max video length',
                        'Videos can be up to 10 minutes long, giving you plenty of room for demos, tutorials, and storytelling.',
                        'timer',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        const isFree = isFreePlan(plan);
                        return isFree ? '2 min' : '10 min';
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Blueprints',
                        'Save your best video structures as reusable templates so every new video has a head start.',
                        'dashboard_customize',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        '100+ premium templates',
                        'Jump-start any video with professionally designed scenes, from social posts to product demos.',
                        'grid_view',
                        '#10B981',
                    )}
                    {generateSimpleRowData(plans, 'preset_themes')}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('AI features')}
                <tr>
                    {generateFeatureCell(
                        'AI voiceover minutes',
                        'Add a professional AI voiceover to any video. Minutes are only counted when you publish, so experimenting is always free.',
                        'record_voice_over',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }

                        let value = plan.features.num_ai_vo_minutes_per_month ?? 0;
                        let unit = 'mins/mo';

                        if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                            value = value * 12;
                            unit = 'mins/yr';
                        }

                        return `${value} ${unit}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Voice cloning',
                        'Record a short sample to create a synthetic copy of your voice, then use it to narrate any video on demand.',
                        'voice_chat',
                        '#8B5CF6',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }
                        const maxClones = plan.features.max_voice_clones;
                        if (!maxClones) {
                            return false;
                        }
                        return `${maxClones} ${pluralize(maxClones, 'voice')}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Translations',
                        'Automatically translate your video text and captions into another language to reach global audiences.',
                        'translate',
                        '#02D3DA',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        const isFree = isFreePlan(plan);
                        if (isFree) {
                            return false;
                        }
                        if (plan.features.max_translations_per_month === null) {
                            return 'Unlimited';
                        }

                        let value = plan.features.max_translations_per_month;
                        let unit = 'month';

                        if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                            value = value * 12;
                            unit = 'year';
                        }

                        return `${value}/${unit}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Animation minutes',
                        'Create stunning 2D and 3D animated explainer videos with AI, no design skills required.',
                        'animation',
                        '#8B5CF6',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }

                        // The plan value is already the per-cycle total (annual plans carry the
                        // full-year amount), so it is shown as-is without an annual multiplier.
                        const minutesPerCycle = plan.features.plan_animation_minutes_per_cycle;

                        if (!minutesPerCycle || minutesPerCycle === 0) {
                            return false;
                        }

                        const isAnnual =
                            isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL;
                        const timeUnit = pluralize(minutesPerCycle, 'min');
                        const unit = isAnnual ? '/yr' : '/mo';

                        return `${minutesPerCycle.toLocaleString()} ${timeUnit}${unit}`;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Automated captions',
                        'Captions are generated automatically from spoken words, boosting accessibility and watch time on silent feeds.',
                        'closed_caption',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        '1000+ lifelike AI voices',
                        'Pick from 1000+ lifelike AI voices across languages, accents, and tones for professional-sounding narration.',
                        'mic',
                        '#EC4899',
                    )}
                    {generateCustomRowData(plans, (plan) => !isFreePlan(plan))}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Premium AI images',
                        'Generate high-quality AI images to bring your videos to life.',
                        'auto_awesome',
                        '#EC4899',
                    )}
                    {generateCustomRowData(plans, (plan) => !isFreePlan(plan))}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Branding')}
                <tr>
                    {generateFeatureCell(
                        'Custom colors',
                        "Override any template's default palette with your exact brand colors.",
                        'palette',
                        '#EC4899',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Font library',
                        'Pick from a curated font library to match your brand tone without needing a designer.',
                        'font_download',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Brand kits',
                        'Save your logo, colors, and fonts in one place. Apply your full brand identity to any video instantly.',
                        'palette',
                        '#EC4899',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Unlimited';
                        }
                        const isFree = isFreePlan(plan);
                        if (isFree) {
                            return <span className="text-muted">&#8212;</span>;
                        }
                        return plan.features.max_brand_kits;
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Upload your own fonts',
                        "Upload your brand's proprietary or licensed fonts so every video stays on-brand.",
                        'upload',
                        '#02D3DA',
                    )}
                    {generateSimpleRowData(plans, 'custom_font')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Watermark upload',
                        'Add your logo as a persistent watermark that appears on every published video.',
                        'branding_watermark',
                        '#8B5CF6',
                    )}
                    {generateSimpleRowData(plans, 'custom_watermark')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Bespoke brand templates',
                        'Our design team builds templates tailored to your brand guidelines, ready for your whole organization to use.',
                        'design_services',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Custom transitions',
                        "Get unique scene transitions crafted to match your brand's visual identity.",
                        'transition_fade',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Bespoke scene designs',
                        'Commission original scene layouts designed exclusively for your brand.',
                        'draw',
                        '#EC4899',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Creative services',
                        "Work directly with Lumen5's in-house designers for ongoing creative support and custom deliverables.",
                        'brush',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Media')}
                <tr>
                    {generateFeatureCell(
                        'Licensed stock library',
                        'Browse millions of images and video clips, fully licensed for commercial use with no copyright concerns.',
                        'photo_library',
                        '#02D3DA',
                    )}
                    {generateSimpleRowData(plans, 'cc_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Media uploads',
                        'Bring in your own footage and photos, and organize them with folders for fast reuse across videos.',
                        'cloud_upload',
                        '#5645F5',
                    )}
                    {generateSimpleRowData(plans, 'upload_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Music uploads',
                        'Add your own music to any video for a fully custom audio experience.',
                        'audio_file',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Icon library',
                        "Add polish to your scenes with thousands of icons from Lumen5's curated library.",
                        'emoji_symbols',
                        '#02D3DA',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Talking head overlays',
                        'Enhance talking head videos with professionally designed overlays: lower thirds, callouts, cutaways, and more.',
                        'recent_actors',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Shutterstock library',
                        'Access the full Shutterstock library of over 50 million premium images and video clips at no extra cost.',
                        'stars',
                        '#F59E0B',
                    )}
                    {generateSimpleRowData(plans, 'shutterstock_unlimited_media')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Shutterstock editorial',
                        "Spend monthly credits on Shutterstock's premium editorial content, including news imagery not available in standard libraries.",
                        'workspace_premium',
                        '#8B5CF6',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Custom';
                        }
                        // for free/Starter, don't show credit listing
                        else if (plan.features.asset_credits <= 5) {
                            return false;
                        } else {
                            let unit = 'month';
                            if (isAnnualPromo || plan.billing_cycle === BillingCycleEnum.ANNUAL) {
                                unit = 'year';
                            }

                            return `${plan.features.public_asset_credits}/${unit}`;
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'DAM integration',
                        'Connect your existing digital asset management system so your team always has the right approved media on hand.',
                        'storage',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Collaboration')}
                <tr>
                    {generateFeatureCell(
                        'Team members',
                        'Invite teammates to create, edit, and share videos together under one account.',
                        'group',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Multiple users';
                        } else if (
                            plan.features.purchased_seat_ceiling > 1 ||
                            plan.features.default_team_seats > 1
                        ) {
                            return 'Multiple users';
                        } else {
                            return 'Single user';
                        }
                    })}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Workspaces',
                        'Organize videos into separate workspaces, useful for managing multiple brands, clients, or departments.',
                        'folder_open',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, (plan) => {
                        if (plan.is_enterprise) {
                            return 'Unlimited';
                        }
                        if (plan.display_name === 'Pro') {
                            return 3;
                        }
                        return false;
                    })}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Team management')}
                <tr>
                    {generateFeatureCell(
                        'Two-factor authentication',
                        'Add a second verification step at login to protect your account from unauthorized access.',
                        'security',
                        '#EF4444',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Brand management',
                        'Lock down colors, fonts, and templates so every video your team makes stays on-brand.',
                        'verified',
                        '#5645F5',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Granular user permissions',
                        'Control who can create, edit, publish, or manage settings, down to the individual user.',
                        'manage_accounts',
                        '#8B5CF6',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Workspace management',
                        'Organize your team into separate workspaces, each with its own members, brand kits, and video library.',
                        'workspaces',
                        '#02D3DA',
                    )}
                    {generateCustomRowData(
                        plans,
                        (plan) => plan.is_enterprise || isBusinessPlan(plan.name),
                    )}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Audit log',
                        'See a full record of who did what and when, essential for compliance, security audits, and accountability.',
                        'history',
                        '#5645F5',
                    )}
                    {generateSimpleRowData(plans, 'has_audit_log')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Single sign-on',
                        "Let employees log in with your company's identity provider. No extra passwords, simpler IT management.",
                        'key',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Video approval workflow',
                        'Require manager sign-off before a video is published, keeping quality control in the right hands.',
                        'approval',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
            <tbody>
                {generateHeaderRowData('Support')}
                <tr>
                    {generateFeatureCell(
                        'Help center access',
                        'Browse hundreds of step-by-step guides, tutorials, and FAQs in our self-serve help center, available 24/7.',
                        'help',
                        '#02D3DA',
                    )}
                    {generateCustomRowData(plans, () => true)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Chat support',
                        'Get fast answers from our support team via live chat, without waiting on email.',
                        'chat',
                        '#10B981',
                    )}
                    {generateSimpleRowData(plans, 'chat_support')}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'White-glove support',
                        'A dedicated Customer Success Manager to onboard your team and help you get the most from Lumen5.',
                        'support_agent',
                        '#5645F5',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Invoiced billing',
                        'Pay by invoice instead of credit card, ideal for procurement teams and enterprise finance workflows.',
                        'receipt',
                        '#8B5CF6',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Procurement support',
                        'We work directly with your procurement team to meet vendor requirements, security reviews, and contract needs.',
                        'handshake',
                        '#F59E0B',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Security & compliance',
                        'Access security questionnaires, compliance documentation, and direct support for IT and legal reviews.',
                        'shield',
                        '#EF4444',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
                <tr>
                    {generateFeatureCell(
                        'Training workshops',
                        'Live training sessions covering everything from getting started to advanced video production workflows.',
                        'school',
                        '#10B981',
                    )}
                    {generateCustomRowData(plans, (plan) => plan.is_enterprise)}
                </tr>
            </tbody>
        </Table>
    );
});

export default ComparisonTable;
